import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import * as annApi from "../../api/annotations";
import { cloudDepthBlob, cloudSummary, type CloudSummary } from "../../api/cloud";
import { listImages } from "../../api/images";
import { listClasses } from "../../api/projects";
import type { ImageOut } from "../../api/types";
import { AnnotationDoc, type Mode } from "../../canvas/doc";
import { CanvasController, type ContextMenuRequest } from "../../canvas/controller";
import { ImageCache, images as imageCache, type Loaded } from "../../canvas/imageCache";
import { Saver, type SaveApi } from "../../canvas/save";
import { clickSelect } from "../../lib/dashboard";
import { PRO_TIP } from "../../lib/edition";
import { ANNOTATE_ITEMS, IMPLEMENTED, hint } from "../../menus";
import { ActionMenu } from "../../ui/ActionMenu";
import { navigate } from "../../router";
import { ContextMenu } from "../../ui/ContextMenu";
import { Splitter, usePanelSize } from "../../ui/Splitter";
import { PANELS } from "../../lib/panelSize";
import { useSession } from "../../state/session";
import { useDialogs } from "../../ui/dialogs";
import { useShell } from "../../ui/shellActions";
import { Icon } from "../../ui/icons";

const AUTOSAVE_MS = 30_000;
const REVIEW_FILTERS: [string, string | null][] = [
  ["All images", null], ["Needs review", "needs_review"], ["Reviewed (used for training)", "reviewed"], ["Not annotated", "unannotated"],
  ["Marked empty", "empty"],
];

const saveApi: SaveApi = {
  list: annApi.listAnnotations, create: annApi.createAnnotation, update: annApi.updateAnnotation, remove: annApi.deleteAnnotation,
};

/** "3 of 5 reviewed" etc. (AnnotationView._load_current_image). `empty`: a person marked the image as holding none of the objects. */
export function reviewState(total: number, reviewed: number, empty = false): string {
  if (total === 0) return empty ? "marked empty ✓ (trains as background)" : "no annotations";
  if (reviewed === 0) return `${total} unreviewed`;
  if (reviewed < total) return `${reviewed} of ${total} reviewed`;
  return `${total} reviewed ✓`;
}

/** One row of the Annotations list, in parts: the class, the shape, and a review pill (none for a person's own shape). */
export function layerParts(kind: string, className: string | null, source: string, reviewed: boolean, rotated = false) {
  const shape = kind === "bbox" ? "Box" : rotated ? "Rotated box" : "Polygon";
  const review = source === "manual" ? null : reviewed ? `${source[0].toUpperCase()}${source.slice(1)} ✓` : "Needs review";
  return { name: className ?? "Unlabeled", shape, review };
}

/** The same row as one line of text: "Widget · Box · Needs review". */
export function layerText(kind: string, className: string | null, source: string, reviewed: boolean, rotated = false): string {
  const p = layerParts(kind, className, source, reviewed, rotated);
  return [p.name, p.shape, p.review].filter(Boolean).join(" · ");
}

/** The review state as a pill colour, matching the gallery's. */
export function reviewTone(total: number, reviewed: number, empty = false): "reviewed" | "review" | "none" {
  if (total === 0) return empty ? "reviewed" : "none";
  return reviewed === total ? "reviewed" : "review";
}

/** Whether the Measure section is open; remembered per browser. Closed by default: most projects never measure. */
const MEASURE_OPEN_KEY = "modelbench.measureOpen";
const readMeasureOpen = () => { try { return localStorage.getItem(MEASURE_OPEN_KEY) === "1"; } catch { return false; } };
const TIP_KEY = "modelbench.annotateTipDismissed";
const readTipDismissed = () => { try { return localStorage.getItem(TIP_KEY) === "1"; } catch { return false; } };

const pct = (x: number, digits = 0) => `${(x * 100).toFixed(digits)}%`;
const thousands = (n: number) => n.toLocaleString("en-US");

/** MainWindow._measure_selected, scale and depth+FOV models: "12.3 × 4.5 mm", squareness and how it was computed. */
export function formatMeasure(r: annApi.MeasureOut): { head: string; squareness: string; how: string } {
  const warn = r.squareness > 0.05 ? "  ⚠ check corners" : "";
  const how = r.model === "scale"
    ? `scale ${r.scale_mm_per_px.toFixed(5)} mm/px`
    : `${(r.depth ?? 0).toFixed(0)} mm depth, ${(r.fov_degrees ?? 0).toFixed(0)}° FOV (= ${r.scale_mm_per_px.toFixed(5)} mm/px)`;
  return { head: `${r.length_mm.toFixed(1)} × ${r.breadth_mm.toFixed(1)} mm`, squareness: `squareness ${pct(r.squareness, 1)}${warn}`, how };
}

/** MainWindow._show_cloud_measurement: three dimensions, and the warnings that decide whether to believe them. */
export function formatCloudMeasure(r: annApi.CloudMeasureOut): { head: string; detail: string; warnings: string[] } {
  const warnings: string[] = [];
  if (r.clipped) warnings.push("⚠ runs off the edge of the sensor — this is the part that stayed in frame, so it reads small");
  const coverage = r.coverage ?? 1;
  if (coverage < 0.55) warnings.push(`⚠ only ${pct(coverage)} of the outline returned any depth — dark or shiny surfaces do this`);
  if ((r.flatness ?? 0) > 12) warnings.push(`⚠ top surface is uneven (±${r.flatness.toFixed(0)} mm) — a bag, a crushed carton, or two objects in one outline`);
  return {
    head: `${r.length_mm.toFixed(1)} × ${r.breadth_mm.toFixed(1)} × ${r.height_mm.toFixed(1)} mm`,
    detail: `from ${thousands(r.points_used)} points, ${pct(coverage)} coverage — no scale or depth needed`,
    warnings,
  };
}

/** What a ToF capture holds, for the status line (GET /images/{id}/cloud/summary). */
export function summaryText(s: CloudSummary): string {
  const plane = s.plane_found ? `ground plane fitted from ${thousands(s.plane_inliers)} points` : `no ground plane — ${s.plane_error ?? "none could be fitted"}`;
  return `Capture: ${thousands(s.valid_points)} valid points (${pct(s.valid_fraction)} of the sensor grid), ${plane}`;
}

const depthCache = new ImageCache(cloudDepthBlob, undefined, 2);

/** main_window.AnnotationView: tool rail, canvas, and the panel on the right. */
export function AnnotationScreen({ projectId, imageId }: { projectId: number; imageId?: number }) {
  const shell = useShell();
  const doc = useMemo(() => new AnnotationDoc(), []);
  const { canEdit, has } = useSession();
  const dlg = useDialogs();
  doc.readOnly = !canEdit;
  useSyncExternalStore(doc.subscribe, doc.getVersion);

  const [list, setList] = useState<ImageOut[]>([]);
  const [idx, setIdx] = useState(0);
  const [review, setReview] = useState<string | null>(null);
  const [status, setStatus] = useState("Ready");
  const [loadedId, setLoadedId] = useState<number | null>(null);
  const [measureModel, setMeasureModel] = useState("scale");
  const [measureOpen, setMeasureOpen] = useState(readMeasureOpen);
  const [tipDismissed, setTipDismissed] = useState(readTipDismissed);
  const [scale, setScale] = useState(0.20445);
  const [depth, setDepth] = useState(1000);
  const [fov, setFov] = useState(60);
  const [depthView, setDepthView] = useState(false);
  const [measured, setMeasured] = useState<ReactNode>("—");
  const [menu, setMenu] = useState<ContextMenuRequest | null>(null);
  const depthRef = useRef(false);
  const closeMenu = useCallback(() => { setMenu(null); rootRef.current?.focus({ preventScroll: true }); }, []);
  const anchor = useRef<number | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const baseRef = useRef<HTMLCanvasElement>(null);
  const overRef = useRef<HTMLCanvasElement>(null);
  const ctl = useRef<CanvasController | null>(null);
  const listRef = useRef<ImageOut[]>([]);
  const idxRef = useRef(0);
  const reviewRef = useRef<string | null>(null);
  const currentId = useRef<number | null>(null);
  const token = useRef(0);
  const busy = useRef(false);
  const measureRef = useRef({ model: "scale", scale: 0.20445, depth: 1000, fov: 60 });
  measureRef.current = { model: measureModel, scale, depth, fov };
  listRef.current = list; idxRef.current = idx; reviewRef.current = review;

  doc.say = setStatus;
  const saver = useMemo(() => new Saver(doc, () => currentId.current, saveApi), [doc]);

  // ── Canvas lifecycle ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const c = new CanvasController(doc, rootRef.current!, baseRef.current!, overRef.current!);
    ctl.current = c;
    c.onContextMenu = setMenu;
    const detach = c.attach();
    return () => { detach(); ctl.current = null; };
  }, [doc]);

  // ── Loading an image ─────────────────────────────────────────────────────────────────────────
  /** AnnotationCanvas._backdrop_bytes: the height view when it is on and the image has a cloud, else the image itself. */
  const backdrop = useCallback(async (id: number): Promise<Loaded> => {
    if (depthRef.current) {
      try { return await depthCache.get(id); }
      catch (e) {                                     // depth is a diagnostic: losing it never stops annotating, but says why
        depthRef.current = false; setDepthView(false);
        doc.say(`No depth view: ${(e as Error).message}`);
      }
    }
    return imageCache.get(id);
  }, [doc]);

  const show = useCallback(async (images: ImageOut[], i: number) => {
    const img = images[i];
    const mine = ++token.current;
    currentId.current = img.id;
    setIdx(i); setLoadedId(null);
    window.history.replaceState(null, "", `#/annotate/${projectId}/${img.id}`);
    doc.clear();
    try {
      const [loaded, anns] = await Promise.all([backdrop(img.id), annApi.listAnnotations(img.id).catch(() => [])]);
      if (mine !== token.current) return;
      ctl.current?.setImage(loaded.bitmap);
      doc.load(anns);
      setLoadedId(img.id);
      if (images[i + 1]) imageCache.prefetch(images[i + 1].id);
      if (images[i - 1]) imageCache.prefetch(images[i - 1].id);
    } catch (e) {
      if (mine === token.current) setStatus(`Error loading image: ${(e as Error).message}`);
    }
  }, [doc, projectId, backdrop]);

  // First load: classes, the image list for the filter, then the image asked for (AnnotationView.load).
  useEffect(() => {
    let alive = true;
    setStatus("");                       // the shortcut hints are in the tips card now; the line is for what just happened
    (async () => {
      let imgs: ImageOut[] = [];
      try { imgs = await listImages(projectId, { review: reviewRef.current }); } catch { imgs = []; }
      try { doc.classes = await listClasses(projectId); } catch { doc.classes = []; }
      if (!alive) return;
      doc.touch();
      setList(imgs);
      if (imgs.length) await show(imgs, Math.max(0, imgs.findIndex((i) => i.id === imageId)));
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // The hash was changed by hand to another image.
  useEffect(() => {
    if (imageId && imageId !== currentId.current && listRef.current.length) {
      const i = listRef.current.findIndex((x) => x.id === imageId);
      if (i >= 0) void goTo(i);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageId]);

  // ── Saving ───────────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const t = window.setInterval(() => { if (doc.dirty) void saver.save(); }, AUTOSAVE_MS);
    const leave = () => { if (doc.dirty) void saver.save(); };
    window.addEventListener("pagehide", leave);
    return () => { window.clearInterval(t); window.removeEventListener("pagehide", leave); leave(); };
  }, [doc, saver]);

  // ── Navigation ───────────────────────────────────────────────────────────────────────────────
  const goTo = useCallback(async (i: number) => {
    if (busy.current) return;
    busy.current = true;
    try { await saver.save(); await show(listRef.current, i); } finally { busy.current = false; }
  }, [saver, show]);

  const next = () => { if (idxRef.current < listRef.current.length - 1) void goTo(idxRef.current + 1); };
  const prev = () => { if (idxRef.current > 0) void goTo(idxRef.current - 1); };

  const changeFilter = async (value: string | null) => {
    if (busy.current) return;
    busy.current = true;
    const keep = currentId.current;
    try {
      await saver.save();
      setReview(value); reviewRef.current = value;
      let imgs: ImageOut[];
      try { imgs = await listImages(projectId, { review: value }); }
      catch (e) { setStatus(`Could not filter images: ${(e as Error).message}`); return; }
      setList(imgs); listRef.current = imgs;
      const label = REVIEW_FILTERS.find(([, v]) => v === value)![0];
      if (!imgs.length) {
        token.current++; currentId.current = null;
        doc.clear(); ctl.current?.setImage(null); setIdx(0); setLoadedId(null);
        setStatus(`No images match '${label}'`);
        return;
      }
      await show(imgs, Math.max(0, imgs.findIndex((i) => i.id === keep)));
      setStatus(`${imgs.length} image${imgs.length !== 1 ? "s" : ""} in '${label}'`);
    } finally { busy.current = false; }
  };

  // ── Tools and class ──────────────────────────────────────────────────────────────────────────
  const setTool = useCallback((m: Mode) => {
    doc.setMode(m);
    if (m === "bbox") setStatus("BBox tool active");
    else if (m === "polygon") setStatus("Polygon tool — click each corner, then Enter, right-click or click the first point to close. Click a finished polygon to correct it.");
    else if (m === "obb") setStatus("Rotated box — drag out a rectangle, then spin it with the round handle or the [ and ] keys");
    rootRef.current?.focus({ preventScroll: true });
  }, [doc]);

  const escape = useCallback(() => {
    if (ctl.current?.escape()) { setStatus("Shape discarded — the tool is still active"); return; }
    doc.setMode("select");
    setStatus("Select/drag tool active");
  }, [doc]);

  const pickClass = useCallback((n: number) => {
    const count = doc.classes.length;
    if (n > count) { setStatus(`No class ${n} — this project has ${count} class(es)`); return; }
    const cls = doc.classes[n - 1];
    doc.currentClassId = cls.id; doc.touch();
    if (doc.selected().length) doc.applyClass(cls.id);
    else setStatus(`Class ${n}: ${cls.name} — new shapes use it`);
  }, [doc]);

  const goDashboard = useCallback(async () => { await saver.save(); navigate({ name: "dashboard" }); }, [saver]);

  /** Mark the open image as holding none of the objects (a background image for training), or take the mark off. */
  const toggleEmpty = useCallback(async () => {
    const id = currentId.current;
    const cur = listRef.current[idxRef.current];
    if (id === null || !cur || cur.id !== id || busy.current) return;
    busy.current = true;
    try {
      await saver.save();
      const makeEmpty = cur.status !== "empty";
      if (makeEmpty) {
        const plan = await annApi.markImagesEmpty(projectId, { image_ids: [id], dry_run: true });
        if (plan.images_skipped_reviewed) {
          setStatus("This image has reviewed annotations. Delete them first if there is really nothing in it.");
          return;
        }
        if (plan.annotations_deleted && !(await dlg.confirm({
          title: "Mark image empty", no: "Cancel",
          text: `Delete the ${plan.annotations_deleted} unreviewed annotation${plan.annotations_deleted === 1 ? "" : "s"} on this image and mark it empty?\n\nTraining uses empty images as background, which teaches the model not to detect anything there.`,
          yes: "Mark empty",
        }))) return;
      }
      await annApi.markImagesEmpty(projectId, { image_ids: [id], empty: makeEmpty });
      const anns = await annApi.listAnnotations(id);
      if (currentId.current !== id) return;
      doc.load(anns);
      const status = makeEmpty ? "empty" : "unannotated";
      const updated = listRef.current.map((x) => (x.id === id ? { ...x, status, annotation_count: anns.length, reviewed_annotation_count: 0 } : x));
      setList(updated); listRef.current = updated;
      setStatus(makeEmpty ? "Marked empty — training will use this image as background" : "No longer marked empty");
    } catch (e) {
      setStatus(`Mark empty failed: ${(e as Error).message}`);
    } finally { busy.current = false; }
  }, [doc, dlg, projectId, saver]);

  // ── Depth view and measuring ─────────────────────────────────────────────────────────────────
  const showSummary = useCallback(async (id: number, prefix = "") => {
    try {
      const sum = await cloudSummary(id);
      if (currentId.current === id) doc.say(prefix + summaryText(sum));
    } catch (e) { if (currentId.current === id) doc.say(prefix + (e as Error).message); }
  }, [doc]);

  /** AnnotationCanvas.set_depth_view: only the backdrop is replaced; a request on an image with no cloud falls back to intensity. */
  const toggleDepth = useCallback(async () => {
    const want = !depthRef.current;
    depthRef.current = want; setDepthView(want);
    const id = currentId.current;
    if (id === null) return;
    try {
      const loaded = want ? await depthCache.get(id).catch((e: Error) => { throw Object.assign(e, { depth: true }); }) : await imageCache.get(id);
      if (currentId.current !== id) return;
      ctl.current?.swapBitmap(loaded.bitmap);
      doc.say(want ? "Depth view — colour is height above the belt" : "Intensity view");
      if (want) void showSummary(id, "Depth view — colour is height above the belt. ");
    } catch (e) {
      if ((e as { depth?: boolean }).depth) {
        depthRef.current = false; setDepthView(false);
        doc.say(`No depth view: ${(e as Error).message}`);
      } else doc.say(`Could not switch view: ${(e as Error).message}`);
    }
  }, [doc, showSummary]);

  const changeMeasureModel = (m: string) => {
    setMeasureModel(m);
    if (m === "cloud" && currentId.current !== null) void showSummary(currentId.current);
  };

  /** MainWindow._measure_selected: the selected shape's physical size, computed by the backend. */
  const measureSelected = useCallback(async () => {
    const sel = doc.selected();
    const model = measureRef.current.model;
    const fromCloud = model === "cloud";
    if (sel.length !== 1) {
      setMeasured(sel.length === 0 ? "Select exactly one four-corner polygon to measure." : `Select one annotation, not ${sel.length} — a measurement belongs to a single face.`);
      return;
    }
    const a = sel[0];
    if (a.kind !== "polygon" && !fromCloud) {
      setMeasured("Bounding boxes cannot be measured — the box around a tilted face is larger than the face. Trace it with the polygon tool.");
      return;
    }
    // The measurement is computed from what the server holds, so it must hold this shape as it is now.
    if (a.serverId === null || doc.dirty) await saver.save();
    if (a.serverId === null) { setMeasured("Save the annotation first, then measure."); return; }
    try {
      if (fromCloud) {
        const r = formatCloudMeasure(await annApi.measureCloud(a.serverId));
        setMeasured(<><b data-testid="measure-head">{r.head}</b><br /><span style={{ fontSize: 10 }}>{r.detail}</span>{r.warnings.map((w) => <span key={w}><br />{w}</span>)}</>);
        return;
      }
      const m = measureRef.current;
      const r = formatMeasure(await annApi.measureAnnotation(a.serverId, model === "scale" ? { scale_mm_per_px: m.scale } : { depth: m.depth, fov_degrees: m.fov }));
      setMeasured(<><b data-testid="measure-head">{r.head}</b><br />{r.squareness}<br /><span style={{ fontSize: 10 }}>{r.how}</span></>);
    } catch (e) { setMeasured(`Measure failed: ${(e as Error).message}`); }
  }, [doc, saver]);

  // ── What the shell can ask of this page ──────────────────────────────────────────────────────
  const actions = useRef<Record<string, (c?: { arg?: number; event?: KeyboardEvent }) => void>>({});
  actions.current = {
    "tool.select": escape,
    "tool.bbox": () => setTool("bbox"),
    "tool.polygon": () => setTool("polygon"),
    "tool.obb": () => setTool("obb"),
    "draw.close": () => { if (doc.mode === "polygon") ctl.current?.closePolygon(); },
    "draw.undo_point": () => ctl.current?.undoPoint(),
    "edit.copy": () => doc.copySelected(),
    "edit.paste": () => doc.paste(currentId.current !== null),
    "edit.duplicate": () => doc.duplicateSelected(12 / Math.max(ctl.current?.vp.scale ?? 1, 1e-6)),   // 12 screen pixels
    "edit.rotate": (c) => {                          // [ and ]: 1 degree, Shift 15
      const e = c?.event;
      const step = e?.shiftKey ? 15 : 1;
      doc.rotateSelected(e?.key === "[" || e?.code === "BracketLeft" ? -step : step);
    },
    "edit.rotate_15": (c) => doc.rotateSelected(c?.event?.key === "{" ? -15 : 15),   // { and } on layouts where Shift+[ types them
    // The keys reach these even when the menu item is greyed out, so they say why instead of calling a route that refuses.
    "measure.selected": () => (has("measure") ? void measureSelected() : doc.say(PRO_TIP)),
    "view.depth": () => (has("tof") ? void toggleDepth() : doc.say(PRO_TIP)),
    "edit.undo": () => doc.undo(),
    "edit.redo": () => doc.redo(),
    "edit.select_all": () => doc.selectAll(),
    "edit.delete": () => doc.deleteSelected(),
    "edit.save": () => void saver.save(),
    "review.mark": () => doc.markSelectedReviewed(),
    "class.apply": () => doc.applyClass(doc.currentClassId),
    "image.next": next,
    "image.prev": prev,
    "view.fit": () => ctl.current?.fit(),
    "view.zoom_in": () => ctl.current?.zoomKey("in"),
    "view.zoom_out": () => ctl.current?.zoomKey("out"),
    "class.pick": (c) => { if (c?.arg) pickClass(c.arg); },
    "nav.dashboard": () => void goDashboard(),
    // Tools ▸ Reset Review State… and friends: save what is open first, then reload it from the server (the canvas
    // holds its own copies of these rows, so their reviewed state would otherwise be the stale one).
    "page.before": async () => { await saver.save(); },
    "page.after": async () => {
      try { doc.classes = await listClasses(projectId); doc.touch(); } catch { /* keep the classes we have */ }   // after the Classes dialog
      let imgs = listRef.current;
      try { imgs = await listImages(projectId, { review: reviewRef.current }); } catch { /* keep the list we have */ }
      setList(imgs); listRef.current = imgs;
      if (imgs.length) await show(imgs, Math.max(0, imgs.findIndex((i) => i.id === currentId.current)));
    },
    "edit.nudge": (c) => {
      const k = c?.event?.key;
      const step = c?.event?.shiftKey ? 10 : 1;
      const d: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
      if (k && d[k]) doc.nudge(d[k][0] * step, d[k][1] * step);
    },
  };
  /** Actions that change the shapes: ignored while an image is loading (see AnnotationDoc.locked). */
  const EDITS = new Set(["draw.close", "draw.undo_point", "edit.undo", "edit.redo", "edit.select_all", "edit.delete", "edit.copy", "edit.paste", "edit.duplicate",
    "edit.rotate", "edit.rotate_15", "edit.nudge", "review.mark", "class.apply", "class.pick", "measure.selected"]);
  useEffect(() => {
    const stable = Object.fromEntries(Object.keys(actions.current).map((k) => [k, (c?: { arg?: number; event?: KeyboardEvent }) => {
      if (EDITS.has(k) && doc.frozen) return;
      actions.current[k](c);
    }]));
    return shell.register(stable, () => {
      const drawing = !!ctl.current?.isDrawing();
      return { hasSelection: doc.selection.size > 0 && !drawing, drawingPolygon: drawing };   // arrows change image rather than nudge while tracing
    });
  }, [shell, doc]);
  useEffect(() => { shell.setChecked({ "view.depth": depthView }); }, [shell, depthView]);

  const [railW, setRailW] = usePanelSize(PANELS.rail);
  const [sideW, setSideW] = usePanelSize(PANELS.side);
  const [layersH, setLayersH] = usePanelSize(PANELS.layers);

  // ── Render ───────────────────────────────────────────────────────────────────────────────────
  const img = list[idx];
  const counts = doc.counts();
  const markedEmpty = !!img && img.status === "empty" && (loadedId !== img.id || counts.total === 0);
  // Drawing on an image replaces its empty mark on the server (and erasing does not bring it back), so the list forgets it too.
  const drawnOnEmpty = !!img && img.status === "empty" && loadedId === img.id && counts.total > 0;
  useEffect(() => {
    if (!drawnOnEmpty || !img) return;
    const updated = listRef.current.map((x) => (x.id === img.id ? { ...x, status: "annotated" } : x));
    setList(updated); listRef.current = updated;
  }, [drawnOnEmpty, img]);
  const state = img ? (loadedId === img.id ? reviewState(counts.total, counts.reviewed, markedEmpty) : reviewState(img.annotation_count, img.reviewed_annotation_count, markedEmpty)) : "";
  const order = doc.anns.map((a) => a.key);
  const rail = (mode: Mode, icon: ReactNode, tip: string, enabled: boolean) => (
    <button key={mode} className="btn tool rail" aria-pressed={doc.mode === mode} disabled={!enabled} title={tip}
      data-testid={`tool-${mode}`} tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={() => setTool(mode)}>{icon}</button>
  );
  const railBtn = (icon: ReactNode, tip: string, on: () => void, test: string, h = 34) => (
    <button className="btn tool rail" style={{ height: h }} title={tip} data-testid={test} tabIndex={-1} onMouseDown={(e) => e.preventDefault()} onClick={on}>{icon}</button>
  );

  return (
    <div className="annot" data-testid="annotate">
      <div className="panel rail-panel" style={{ flexBasis: railW }} data-testid="rail-panel">
        {rail("select", <Icon name="hand" size={20} />, `Select / Pan  (${hint("tool.select")})\nDrag empty space or an unselected shape to pan; click a shape\nto select it, then drag to move it. Space+drag pans from any tool.`, true)}
        {rail("bbox", <Icon name="box" size={20} />, `Bounding box  (${hint("tool.bbox")})`, canEdit)}
        {rail("polygon", <Icon name="polygon" size={20} />, `Polygon  (${hint("tool.polygon")}) — click each corner, Enter or right-click to close.\nClick a finished polygon to correct it: drag a corner,\ndouble-click an edge to add one, Alt+click a corner to remove it,\ndrag the round grip or press [ / ] to rotate.`, canEdit)}
        {rail("obb", <Icon name="rotatedBox" size={20} />, `Rotated box — drag, then spin  (${hint("tool.obb")})`, canEdit)}
        <div className="rail-sep" />
        {canEdit && railBtn(<Icon name="undo" size={18} />, `Undo  (${hint("edit.undo")})`, () => shell.run("edit.undo"), "rail-undo")}
        {canEdit && railBtn(<Icon name="redo" size={18} />, `Redo  (${hint("edit.redo")})`, () => shell.run("edit.redo"), "rail-redo")}
        <div style={{ flex: 1 }} />
        {railBtn(<Icon name="zoomIn" size={18} />, `Zoom in  (${hint("view.zoom_in")}, pinch, wheel)`, () => ctl.current?.zoomButton("in"), "zoom-in")}
        {railBtn(<Icon name="zoomOut" size={18} />, `Zoom out  (${hint("view.zoom_out")})`, () => ctl.current?.zoomButton("out"), "zoom-out")}
        {railBtn(<Icon name="fit" size={18} />, `Fit image  (${hint("view.fit")})`, () => ctl.current?.fit(), "zoom-fit")}
        <ActionMenu label={<Icon name="more" size={18} />} items={ANNOTATE_ITEMS()} onAction={(a) => shell.run(a)} testId="menu-annotate"
          className="btn tool rail" drop="right" title="More actions"
          state={{ admin: false, canEdit, annotateView: true, hasProject: true, has, checked: { "view.depth": depthView },
            implemented: new Set([...IMPLEMENTED, ...Object.keys(actions.current)]) }} />
        {railBtn(<Icon name="keyboard" size={18} />, `Keyboard shortcuts  (${hint("help.shortcuts")})`, () => shell.run("help.shortcuts"), "rail-shortcuts")}
      </div>
      <Splitter axis="x" spec={PANELS.rail} size={railW} onSize={setRailW} label="Resize tool rail" testId="rail-splitter" />

      <div className="canvas-wrap" ref={rootRef} tabIndex={0} data-canvas data-testid="canvas" aria-label="Annotation canvas">
        <canvas ref={baseRef} data-testid="canvas-image" />
        <canvas ref={overRef} data-testid="canvas-overlay" />
      </div>

      {menu && <ContextMenu {...menu} onClose={closeMenu} />}

      <Splitter axis="x" spec={PANELS.side} size={sideW} onSize={setSideW} sign={-1} label="Resize side panel" testId="side-splitter" />
      <div className="panel side-panel" style={{ flexBasis: sideW, width: sideW }} data-testid="side-panel">
        <div className="img-head" data-testid="image-head">
          <button className="btn ghost icon-btn" title="Previous image (Left arrow)" data-testid="prev-image" tabIndex={-1} disabled={idx <= 0} onClick={prev} aria-label="Previous image"><Icon name="chevronLeft" /></button>
          <div className="img-head-mid">
            <span className="img-head-caption">Image</span>
            <span className="img-head-count" data-testid="image-counter">{list.length ? `${idx + 1} / ${list.length}` : "0 / 0"}</span>
          </div>
          <button className="btn ghost icon-btn" title="Next image (Right arrow)" data-testid="next-image" tabIndex={-1} disabled={idx >= list.length - 1} onClick={next} aria-label="Next image"><Icon name="chevronRight" /></button>
        </div>
        <div className="img-name" title="The image being annotated" data-testid="filename">
          {img ? (<><b>{img.filename}</b>
            <span className={`pill ${loadedId === img.id ? reviewTone(counts.total, counts.reviewed, markedEmpty) : reviewTone(img.annotation_count, img.reviewed_annotation_count, markedEmpty)}`}
              data-testid="review-state">{state}</span></>) : "—"}
        </div>
        <label className="label" htmlFor="review-filter">Showing</label>
        <select id="review-filter" className="select" value={review ?? ""} data-testid="review-filter" title="Narrow the set you page through. 'Reviewed' is exactly what training will use."
          onChange={(e) => void changeFilter(e.target.value || null)}>
          {REVIEW_FILTERS.map(([label, v]) => <option key={label} value={v ?? ""}>{label}</option>)}
        </select>

        <div className="label">Classes <span className="label-hint">{doc.selection.size ? "click to relabel the selection" : "click or press 1–9"}</span></div>
        <div className="class-list" role="radiogroup" aria-label="Active class" data-testid="class-list">
          {[...doc.classes.map((c, i) => ({ id: c.id as number | null, name: c.name, color: c.color_hex, key: i < 9 ? String(i + 1) : "" })),
            { id: null as number | null, name: "No class", color: "", key: "" }].map((c) => {
            const n = doc.anns.filter((a) => a.classId === c.id).length;
            return (
              <button key={c.id ?? "none"} type="button" role="radio" aria-checked={doc.currentClassId === c.id} className={`class-row${c.id === null ? " no-class" : ""}`}
                data-testid="class-row" tabIndex={-1} onMouseDown={(e) => e.preventDefault()}
                title={c.id === null ? "New shapes get no class; with a selection, takes the class off it (L)" : `${c.name}${c.key ? ` (key ${c.key})` : ""}: new shapes use it; with a selection, relabels it`}
                onClick={() => { doc.currentClassId = c.id; doc.touch(); if (doc.selected().length) doc.applyClass(c.id); }}>
                <span className="swatch" style={c.color ? { background: c.color } : undefined} />
                <span className="class-name">{c.name}</span>
                {n > 0 && <span className="class-count" title={`${n} on this image`}>×{n}</span>}
                {c.key && <kbd>{c.key}</kbd>}
              </button>
            );
          })}
        </div>

        <div className="label">Annotations <span className="label-hint">{counts.total ? `${counts.total} on this image` : ""}</span></div>
        <ul className="list layers" style={{ height: layersH }} role="listbox" aria-multiselectable="true" data-testid="layers"
          onMouseLeave={() => doc.setHovered(null)}>
          {doc.anns.map((a) => {
            const p = layerParts(a.kind, doc.classOf(a)?.name ?? null, a.source, a.reviewed, !!a.obb);
            return (
              <li key={a.key} role="option" aria-selected={doc.selection.has(a.key)} data-testid="layer" onMouseEnter={() => doc.setHovered(a.key)}
                title={[p.name, p.shape, p.review].filter(Boolean).join(" · ")}
                onClick={(e) => {
                  const r = clickSelect(doc.selection as ReadonlySet<number>, order, anchor.current, a.key, { ctrl: e.ctrlKey || e.metaKey, shift: e.shiftKey });
                  anchor.current = r.anchor; doc.select(r.selected);
                }}>
                <Icon name={a.kind === "bbox" ? "box" : a.obb ? "rotatedBox" : "polygon"} size={14} className="layer-icon" />
                <span className="swatch" style={{ background: doc.colorOf(a) }} />
                <span className="layer-name">{p.name}</span>
                <span className="layer-shape"> · {p.shape}</span>
                {p.review && <><span className="sep-hidden"> · </span><span className={`pill ${a.reviewed ? "reviewed" : "review"}`}>{p.review}</span></>}
              </li>
            );
          })}
          {doc.anns.length === 0 && <li className="layers-empty muted" aria-disabled="true">Nothing drawn yet. Pick a class, then B for a box or P for a polygon.</li>}
        </ul>
        <Splitter axis="y" spec={PANELS.layers} size={layersH} onSize={setLayersH} label="Resize annotations list" testId="layers-splitter" />
        <button className="btn" data-testid="mark-reviewed" title="Mark selected auto-annotations reviewed without editing them (R)" onClick={() => doc.markSelectedReviewed()}><Icon name="check" />Mark reviewed</button>
        <button className="btn danger" data-testid="delete-selected" onClick={() => doc.deleteSelected()}><Icon name="trash" />Delete selected</button>
        <button className="btn" data-testid="mark-empty" disabled={!canEdit || !img || loadedId !== img.id || (!markedEmpty && counts.total > 0 && counts.reviewed > 0)}
          title={markedEmpty
            ? "Take the empty mark off this image"
            : "None of your objects is in this image. Training uses it as background, which stops the model detecting things on an empty scene. Unreviewed annotations on it are deleted."}
          onClick={() => void toggleEmpty()}><Icon name="eyeOff" />{markedEmpty ? "Unmark empty" : "Mark image empty"}</button>

        {has("measure") && (
          <details className="side-section" open={measureOpen} data-testid="measure-section"
            onToggle={(e) => { const o = (e.currentTarget as HTMLDetailsElement).open; setMeasureOpen(o); try { localStorage.setItem(MEASURE_OPEN_KEY, o ? "1" : "0"); } catch { /* not remembered */ } }}>
            <summary data-testid="measure-toggle">Measure (L × B)</summary>
          <select className="select" value={measureModel} data-testid="measure-model" onChange={(e) => changeMeasureModel(e.target.value)}
            title={"Scale: the image was already rectified to a fixed ground resolution (a dimensioning system does this using its own height reading).\nDepth + FOV: a straight photograph, where size depends on distance."}>
            <option value="scale">Fixed scale (mm per pixel)</option>
            <option value="pinhole">Camera distance and field of view</option>
            <option value="cloud">3D capture (point cloud)</option>
          </select>
          {measureModel === "scale" && (
            <div className="row" style={{ flexWrap: "nowrap" }}><span>Scale:</span>
              <input className="input" type="number" step="0.00001" min={0.000001} max={1000} value={scale} onChange={(e) => setScale(Number(e.target.value))} data-testid="measure-scale"
                title="Millimetres per pixel for this image set. For orthorectified images this is a single constant and is the whole camera model." /><span className="muted">mm/px</span></div>
          )}
          {measureModel === "pinhole" && (<>
            <div className="row" style={{ flexWrap: "nowrap" }}><span>Depth:</span>
              <input className="input" type="number" step="0.1" min={1} max={100000} value={depth} onChange={(e) => setDepth(Number(e.target.value))} data-testid="measure-depth"
                title="Distance from the camera to the package face, from the ToF sensor. The answer comes back in whatever unit you enter here." /><span className="muted">mm</span></div>
            <div className="row" style={{ flexWrap: "nowrap" }}><span>FOV:</span>
              <input className="input" type="number" step="0.1" min={1} max={179} value={fov} onChange={(e) => setFov(Number(e.target.value))} data-testid="measure-fov"
                title="The camera's horizontal field of view, as quoted on its datasheet. The focal length in pixels is derived from this." /><span className="muted">°</span></div>
          </>)}
          <button className="btn" data-testid="measure-btn" onClick={() => void measureSelected()}
            title={`Physical size of the selected four-corner shape, by the model above (${hint("measure.selected")})`}><Icon name="ruler" />Measure selected</button>
          <div className="muted" data-testid="measure-label" style={{ userSelect: "text" }}>{measured}</div>
          </details>
        )}
        {!tipDismissed && (
          <div className="annot-tip" data-testid="annotate-tip">
            <span><b>Tips</b> 1–9 pick a class · B box · P polygon · R mark reviewed · ←/→ next image · ? all shortcuts</span>
            <button className="btn ghost icon-btn" aria-label="Dismiss tips" data-testid="annotate-tip-close"
              onClick={() => { setTipDismissed(true); try { localStorage.setItem(TIP_KEY, "1"); } catch { /* shown again next time */ } }}><Icon name="x" size={14} /></button>
          </div>
        )}
        <div className="muted" data-testid="annotate-status" role="status">{status}</div>
      </div>
    </div>
  );
}
